import type { AnyObject } from "@similarweb/pro-logic-components";

import { PercentPointCell } from "app/components/React/Table/cells/PercentPointCell";
import { translate } from "app/filters/i18nFilter";

import { BenchmarkGranularity } from "../../../services/api/constants";
import WebsiteWithFaviconCell from "../../Share/WebsiteWithFaviconCell/WebsiteWithFaviconCell";
import type { CompetitorsDataTypeMap } from "../CompetitorsModal/type";
import { CustomChangeCell } from "../MetricElements/TableView/cells/CustomChangeCell";
import type { BenchmarkColumnSchema } from "../MetricElements/TableView/config";
import { generateColumns, generateColumnWithChildren } from "../MetricElements/TableView/config";

const DOMAIN_WIDTH = 240;
const DOMAIN_WIDTH_LARGE = 500;
const DEFAULT_WIDTH = 120;
const WIDTH_SMALL = 100;

export type ModalConfigSchema = {
    granularity: BenchmarkGranularity;
    isRelative: boolean;
    metricName: keyof CompetitorsDataTypeMap;
};

export type ModalColumnsConfigFn = (config: ModalConfigSchema) => BenchmarkColumnSchema[];

const shareChangeConfig: Partial<BenchmarkColumnSchema> = {
    width: WIDTH_SMALL,
    get displayName() {
        return translate("activation.home.widget.body.table.column.share.change");
    },
    get title() {
        return translate("activation.home.widget.body.table.column.share.change");
    },
    align: "center",
};

const reachChangeConfig: Partial<BenchmarkColumnSchema> = {
    width: WIDTH_SMALL,
    get displayName() {
        return translate("activation.home.widget.body.table.column.reach.change");
    },
    get title() {
        return translate("activation.home.widget.body.table.column.reach.change");
    },
    align: "center",
};

const getRankingModalColumns = ({ granularity, isRelative, metricName }: ModalConfigSchema) => {
    const popColumn = granularity === BenchmarkGranularity.MONTHLY ? "mom" : "wow";
    const render = (value: string, row: AnyObject) =>
        isRelative ? (
            <PercentPointCell
                value={value}
                row={row}
            />
        ) : (
            <CustomChangeCell
                value={value}
                row={row}
            />
        );

    return generateColumns(["rank", "domain", popColumn, "yoy"], {
        domain: {
            width: DOMAIN_WIDTH_LARGE,
            render: (value: string, row: AnyObject) => (
                <WebsiteWithFaviconCell
                    value={value}
                    row={row}
                    primaryIcon="checked-circle"
                    showTooltip={false}
                    metricName={metricName}
                />
            ),
        },
        mom: {
            width: DEFAULT_WIDTH,
            render,
        },
        wow: {
            width: DEFAULT_WIDTH,
            render,
        },
        yoy: {
            width: DEFAULT_WIDTH,
            render,
        },
    });
};

export const getModalColumns: Record<string, ModalColumnsConfigFn> = {
    trafficShare: ({ granularity, metricName }: ModalConfigSchema) => {
        const popColumnName = granularity === BenchmarkGranularity.MONTHLY ? "mom" : "wow";
        const popParentName = granularity === BenchmarkGranularity.MONTHLY ? "monthly" : "weekly";
        const currentPeriodColumn = generateColumnWithChildren("currentPeriod", ["trafficShare"], {
            trafficShare: {
                get displayName() {
                    return translate("activation.home.widget.body.table.column.traffic.share");
                },
                get title() {
                    return translate("activation.home.widget.body.table.column.traffic.share");
                },
            },
        });
        const popColumn = generateColumnWithChildren(popParentName, [popColumnName, "rankChange"], {
            mom: shareChangeConfig,
            wow: shareChangeConfig,
            rankChange: {
                width: WIDTH_SMALL,
                dataIndex: "popRankChange",
                key: "popRankChange",
            },
        });
        const yearlyColumn = generateColumnWithChildren("yearly", ["yoy", "rankChange"], {
            yoy: shareChangeConfig,
            rankChange: {
                width: WIDTH_SMALL,
                dataIndex: "yoyRankChange",
                key: "yoyRankChange",
            },
        });

        return generateColumns(["rank", "domain", currentPeriodColumn, popColumn, yearlyColumn, "trends"], {
            domain: {
                width: DOMAIN_WIDTH,
                render: (value: string, row: AnyObject) => (
                    <WebsiteWithFaviconCell
                        value={value}
                        row={row}
                        primaryIcon="checked-circle"
                        showTooltip={false}
                        metricName={metricName}
                    />
                ),
            },
            trafficShare: {
                width: DEFAULT_WIDTH,
            },
            trends: {
                width: DEFAULT_WIDTH,
                align: "left",
            },
        });
    },
    audienceReach: ({ metricName }: ModalConfigSchema) => {
        const currentPeriodColumn = generateColumnWithChildren("currentPeriod", ["audienceReach"], {
            audienceReach: {
                width: DEFAULT_WIDTH,
                align: "center",
            },
        });
        const monthlyColumn = generateColumnWithChildren("monthly", ["mom", "rankChange"], {
            mom: reachChangeConfig,
            rankChange: {
                width: WIDTH_SMALL,
                dataIndex: "popRankChange",
                key: "popRankChange",
            },
        });
        const yearlyColumn = generateColumnWithChildren("yearly", ["yoy", "rankChange"], {
            yoy: reachChangeConfig,
            rankChange: {
                width: WIDTH_SMALL,
                dataIndex: "yoyRankChange",
                key: "yoyRankChange",
            },
        });

        return generateColumns(["rank", "domain", currentPeriodColumn, monthlyColumn, yearlyColumn, "trends"], {
            domain: {
                width: DOMAIN_WIDTH,
                render: (value: string, row: AnyObject) => (
                    <WebsiteWithFaviconCell
                        value={value}
                        row={row}
                        primaryIcon="checked-circle"
                        showTooltip={false}
                        metricName={metricName}
                    />
                ),
            },
            trends: {
                width: DEFAULT_WIDTH,
                align: "left",
            },
        });
    },
    visits: getRankingModalColumns,
    visitDuration: getRankingModalColumns,
    bounceRate: getRankingModalColumns,
    pagesPerVisit: getRankingModalColumns,
    pageViews: getRankingModalColumns,
};
